import React, { useMemo, useState } from 'react';
import { InputNumber, Button, Space, Input, Tag } from 'antd';
import { DeleteOutlined, UploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { handleVariantImageUpload } from '../../util/variantUtils';
import { validateVariantPricing } from '../../util/variantValidation';
import { useUploadConfig } from '../../util/useUploadConfig';
import Notify from '../../util/Notify';
import { getImageUrl } from '../SafeImage';

const VariantCard = ({ variant, isAutoCalculate, isBaseVariant, onUpdate, onDelete }) => {
    const [uploading, setUploading] = useState(false);
    const { maxImageSizeMB } = useUploadConfig();
    
    const errors = useMemo(() => validateVariantPricing(variant), [variant]);
    const hasErrors = errors.length > 0;

    const handleChange = (field, value) => {
        onUpdate({ ...variant, [field]: value });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            setUploading(true);
            const url = await handleVariantImageUpload(file);
            handleChange('imageUrl', url);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || "Image upload failed";
            Notify.error(errorMsg);
        } finally {
            setUploading(false);
        }
    };

    // Derived states based on Pricing Mode
    const disablePrice = isAutoCalculate && !isBaseVariant;
    const disableDiscount = isAutoCalculate;

    return (
        <div style={{
            background: '#ffffff',
            border: `1px solid ${hasErrors ? '#fca5a5' : '#e2e8f0'}`,
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
            position: 'relative'
        }}>
            {/* Header: Variant Value & Delete */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>Variant</span>
                    <Tag color="blue" style={{ fontWeight: 700, fontSize: '14px', padding: '2px 10px', borderRadius: '6px', margin: 0 }}>
                        {variant.value}
                    </Tag>
                    {isAutoCalculate && isBaseVariant && (
                        <Tag color="green" style={{ fontWeight: 700, fontSize: '10px', borderRadius: '4px', margin: 0 }}>
                            BASE MARGIN
                        </Tag>
                    )}
                </div>
                <Button type="text" danger icon={<DeleteOutlined />} onClick={onDelete} size="small" />
            </div>

            {/* Form Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '16px' }}>
                {/* MRP */}
                <div>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#475569', marginBottom: '6px' }}>MRP <span style={{ color: '#ef4444' }}>*</span></div>
                    <InputNumber 
                        prefix="₹" 
                        value={variant.mrp} 
                        onChange={v => handleChange('mrp', v)} 
                        style={{ width: '100%', borderRadius: '8px' }} 
                        placeholder="0.00"
                    />
                </div>

                {/* Selling Price */}
                <div>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#475569', marginBottom: '6px' }}>Selling Price <span style={{ color: '#ef4444' }}>*</span></div>
                    <InputNumber 
                        prefix="₹" 
                        value={variant.price} 
                        onChange={v => handleChange('price', v)} 
                        style={{ width: '100%', borderRadius: '8px', color: disablePrice ? '#94a3b8' : '#FF8800', fontWeight: 700 }} 
                        placeholder="0.00"
                        disabled={disablePrice}
                    />
                </div>

                {/* Discount (Read Only usually) */}
                <div>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#475569', marginBottom: '6px' }}>Discount %</div>
                    <InputNumber 
                        value={variant.discount} 
                        onChange={v => handleChange('discount', v)} 
                        style={{ width: '100%', borderRadius: '8px', background: '#f0fdf4', borderColor: '#bbf7d0', color: '#16a34a', fontWeight: 800 }} 
                        formatter={value => `${value || 0}%`}
                        parser={value => value.replace('%', '')}
                        disabled={disableDiscount}
                    />
                </div>

                {/* Stock */}
                <div>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#475569', marginBottom: '6px' }}>Stock <span style={{ color: '#ef4444' }}>*</span></div>
                    <InputNumber 
                        value={variant.stock} 
                        onChange={v => handleChange('stock', v)} 
                        style={{ width: '100%', borderRadius: '8px' }} 
                        placeholder="Qty"
                    />
                </div>

                {/* Image */}
                <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#475569', marginBottom: '6px' }}>Variant Image <span style={{ color: '#ef4444', fontSize: '9px', fontWeight: 700, marginLeft: '4px' }}>(Max {maxImageSizeMB}MB)</span></div>
                    <Space.Compact style={{ width: '100%', maxWidth: '400px' }}>
                        <Input value={variant.imageUrl} onChange={e => handleChange('imageUrl', e.target.value)} placeholder="Image URL" style={{ borderRadius: '8px 0 0 8px' }} />
                        <Button icon={<UploadOutlined />} loading={uploading} onClick={() => document.getElementById(`upload-${variant.value}`).click()} style={{ borderRadius: '0 8px 8px 0' }} />
                        <input type="file" id={`upload-${variant.value}`} hidden onChange={handleImageUpload} accept="image/*" />
                    </Space.Compact>
                    {variant.imageUrl && (
                        <div style={{ marginTop: '8px' }}>
                            <img src={getImageUrl(variant.imageUrl)} alt={variant.value} style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                        </div>
                    )}
                </div>
            </div>

            {/* Inline Validation Errors */}
            {hasErrors && (
                <div style={{ marginTop: '12px', padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px' }}>
                    {errors.map((err, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444', fontSize: '12px', fontWeight: 600 }}>
                            <ExclamationCircleOutlined /> {err}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default React.memo(VariantCard);
